import React, { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ScopeProvider } from './contexts/ScopeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import PrivateRoute from './components/PrivateRoute';
import GuestOnlyRoute from './components/GuestOnlyRoute';
import AdminRoute from './components/AdminRoute';
import ErrorBoundary from './components/ErrorBoundary';
import SkipLink from './components/SkipLink';
import RouteLoadingScreen from './components/RouteLoadingScreen';
import { HelmetProvider } from 'react-helmet-async';
import { REGISTRATION_ENABLED, PURCHASES_ENABLED } from './config/productFlags';

// Lazy load pages for code splitting (FASE 11 - Optimización React)
const LandingPage = lazy(() => import('./pages/LandingPage'));
const EnterprisePage = lazy(() => import('./pages/EnterprisePage'));
const PrinciplesPage = lazy(() => import('./pages/PrinciplesPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'));
const InviteAcceptPage = lazy(() => import('./pages/InviteAcceptPage'));
const Home = lazy(() => import('./pages/Home'));
const ChatWorkspace = lazy(() => import('./pages/ChatWorkspace'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const NewsletterSection = lazy(() => import('./pages/NewsletterSection'));
const NewsletterReport = lazy(() => import('./pages/NewsletterReportPage'));
const PremiumReportViewer = lazy(() => import('./pages/PremiumReportViewer'));
const ResearchCatalogPage = lazy(() => import('./pages/ResearchCatalogPage'));
const SharedVisualization = lazy(() => import('./pages/SharedVisualization'));
const PackagesMarketplace = lazy(() => import('./pages/PackagesMarketplace'));
const MyPurchases = lazy(() => import('./pages/MyPurchases'));
const UpgradePage = lazy(() => import('./pages/UpgradePage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const AlertsPage = lazy(() => import('./pages/AlertsPage'));
const EconomicCalendar = lazy(() => import('./pages/EconomicCalendarPage'));
const DataNotesPage = lazy(() => import('./pages/DataNotesPage'));
const DataNoteDetailPage = lazy(() => import('./pages/DataNoteDetailPage'));
const VisualizationPage = lazy(() => import('./pages/VisualizationPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AtlasIndexPage = lazy(() => import('./atlas/pages/AtlasIndexPage'));
const AtlasTopicPage = lazy(() => import('./atlas/pages/AtlasTopicPage'));
const AtlasExplorerPage = lazy(() => import('./atlas/pages/AtlasExplorerPage'));
const AtlasViewsPage = lazy(() => import('./atlas/pages/AtlasViewsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const LegalDocumentPage = lazy(() => import('./pages/LegalDocumentPage'));
const DesignLabRoutes = lazy(() => import('./pages/design-lab'));

/** Design Lab: on in development, or when REACT_APP_DESIGN_LAB=true (never required in prod). */
const DESIGN_LAB_ENABLED =
  process.env.REACT_APP_DESIGN_LAB === 'true' || process.env.NODE_ENV === 'development';

const LoadingFallback = () => <RouteLoadingScreen />;

const RouteBoundary = ({ children }) => <ErrorBoundary>{children}</ErrorBoundary>;

const wrapPrivate = (element) => (
  <RouteBoundary>
    <PrivateRoute>{element}</PrivateRoute>
  </RouteBoundary>
);

const wrapAdmin = (element) => (
  <RouteBoundary>
    <AdminRoute>{element}</AdminRoute>
  </RouteBoundary>
);

/** /topics became the Atlas; old links keep working (slugs are unchanged, the query string is preserved). */
const TopicsIndexRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/atlas${search}`} replace />;
};
const TopicRedirect = () => {
  const { slug } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/atlas/${encodeURIComponent(slug)}${search}`} replace />;
};

const wrapPublic = (element) => <RouteBoundary>{element}</RouteBoundary>;

const wrapGuestOnly = (element) => (
  <RouteBoundary>
    <GuestOnlyRoute>{element}</GuestOnlyRoute>
  </RouteBoundary>
);

function App() {
  const { t } = useTranslation();
  return (
    <>
      <SkipLink targetId="main-content" label={t('layout.skipLink')} />
      <HelmetProvider>
        <ThemeProvider>
        <AuthProvider>
          <ScopeProvider>
          <ToastProvider>
            <Router>
              <div className="app-routes-root">
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={wrapPublic(<LandingPage />)} />
                    <Route path="/empresas" element={wrapPublic(<EnterprisePage />)} />
                    <Route path="/principios" element={wrapPublic(<PrinciplesPage />)} />
                    <Route path="/pricing" element={<Navigate to="/empresas" replace />} />
                    <Route path="/privacy" element={wrapPublic(<LegalDocumentPage doc="privacy" />)} />
                    <Route path="/terms" element={wrapPublic(<LegalDocumentPage doc="terms" />)} />
                    <Route path="/login" element={wrapGuestOnly(<LoginPage />)} />
                    {REGISTRATION_ENABLED ? (
                      <Route path="/signup" element={wrapGuestOnly(<SignupPage />)} />
                    ) : (
                      <Route path="/signup" element={<Navigate to="/empresas" replace />} />
                    )}
                    <Route path="/reset-password" element={wrapPublic(<PasswordResetPage />)} />
                    <Route path="/accept-invite" element={wrapPublic(<InviteAcceptPage />)} />
                    <Route path="/share/:token" element={wrapPublic(<SharedVisualization />)} />

                    {/* Private routes */}
                    <Route path="/home" element={wrapPrivate(<Home />)} />
                    <Route path="/atlas" element={wrapPrivate(<AtlasIndexPage />)} />
                    <Route path="/atlas/explorador" element={wrapPrivate(<AtlasExplorerPage />)} />
                    <Route path="/atlas/vistas" element={wrapPrivate(<AtlasViewsPage />)} />
                    <Route path="/atlas/:slug" element={wrapPrivate(<AtlasTopicPage />)} />
                    <Route path="/topics" element={wrapPrivate(<TopicsIndexRedirect />)} />
                    <Route path="/topics/:slug" element={wrapPrivate(<TopicRedirect />)} />
                    <Route path="/dashboard" element={<Navigate to="/home" replace />} />
                    <Route path="/visualization" element={wrapPrivate(<VisualizationPage />)} />
                    {/* Compare and history are now panels inside /visualization, not separate screens. */}
                    <Route path="/visualization/history" element={<Navigate to="/visualization" replace />} />
                    <Route path="/compare" element={<Navigate to="/visualization" replace />} />
                    <Route path="/workspace" element={wrapPrivate(<ChatWorkspace />)} />
                    <Route path="/newsletters" element={wrapPrivate(<NewsletterSection />)} />
                    <Route path="/newsletter/:id" element={wrapPrivate(<NewsletterReport />)} />
                    <Route path="/data-notes" element={wrapPrivate(<DataNotesPage />)} />
                    <Route path="/data-notes/:slug" element={wrapPrivate(<DataNoteDetailPage />)} />
                    <Route path="/reports/premium" element={wrapPrivate(<PremiumReportViewer />)} />
                    <Route path="/research" element={wrapPrivate(<ResearchCatalogPage />)} />
                    <Route path="/upgrade" element={wrapPrivate(<UpgradePage />)} />
                    {PURCHASES_ENABLED ? (
                      <>
                        <Route path="/packages" element={wrapPrivate(<PackagesMarketplace />)} />
                        <Route path="/packages/my-purchases" element={wrapPrivate(<MyPurchases />)} />
                      </>
                    ) : (
                      <>
                        <Route path="/packages" element={<Navigate to="/empresas" replace />} />
                        <Route path="/packages/my-purchases" element={<Navigate to="/empresas" replace />} />
                      </>
                    )}
                    <Route path="/notifications" element={wrapPrivate(<NotificationsPage />)} />
                    <Route path="/alerts" element={wrapPrivate(<AlertsPage />)} />
                    <Route path="/calendar" element={wrapPrivate(<EconomicCalendar />)} />
                    <Route path="/settings" element={wrapPrivate(<SettingsPage />)} />

                    {/* Admin routes */}
                    <Route path="/admin" element={wrapAdmin(<AdminPanel />)} />

                    {/* Design Lab v3 — isolated prototypes; not production chrome */}
                    {DESIGN_LAB_ENABLED ? (
                      <Route path="/design-lab/*" element={wrapPublic(<DesignLabRoutes />)} />
                    ) : null}

                    {/* 404 */}
                    <Route path="*" element={wrapPublic(<NotFoundPage />)} />
                  </Routes>
                </Suspense>
              </div>
            </Router>
          </ToastProvider>
          </ScopeProvider>
        </AuthProvider>
        </ThemeProvider>
      </HelmetProvider>
    </>
  );
}

export default App;
